import { z } from 'zod';

const YEAR_MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Month offset from the project start date. Month 0 is the start month.
 * The engine works exclusively in month indexes; `Date` only appears at the UI/chart edge.
 */
export const monthIndexSchema = z.number().int().nonnegative().brand<'MonthIndex'>();
export type MonthIndex = z.infer<typeof monthIndexSchema>;

/** Calendar month as an ISO-like `YYYY-MM` string, e.g. `2026-09`. */
export const yearMonthSchema = z
  .string()
  .regex(YEAR_MONTH_RE, 'Expected YYYY-MM')
  .brand<'YearMonth'>();
export type YearMonth = z.infer<typeof yearMonthSchema>;

export function isYearMonth(value: string): value is YearMonth {
  return YEAR_MONTH_RE.test(value);
}

export function monthIndex(n: number): MonthIndex {
  if (!Number.isInteger(n) || n < 0) {
    throw new RangeError(`Invalid month index: ${String(n)}`);
  }
  return n as MonthIndex;
}

export function yearMonth(year: number, month: number): YearMonth {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) {
    throw new RangeError(`Invalid year: ${String(year)}`);
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`Invalid month: ${String(month)}`);
  }
  return `${String(year)}-${String(month).padStart(2, '0')}` as YearMonth;
}

export function parseYearMonth(ym: YearMonth): { year: number; month: number } {
  const match = YEAR_MONTH_RE.exec(ym);
  if (!match) {
    throw new RangeError(`Invalid YearMonth: ${ym}`);
  }
  return { year: Number(match[1]), month: Number(match[2]) };
}

/** Absolute month count since year 0; used for arithmetic only. */
function toAbsolute(ym: YearMonth): number {
  const { year, month } = parseYearMonth(ym);
  return year * 12 + (month - 1);
}

function fromAbsolute(abs: number): YearMonth {
  return yearMonth(Math.floor(abs / 12), (abs % 12) + 1);
}

export function addMonths(ym: YearMonth, months: number): YearMonth {
  return fromAbsolute(toAbsolute(ym) + months);
}

/** Month index of `ym` relative to `start`. Negative when `ym` precedes `start`. */
export function monthsBetween(start: YearMonth, ym: YearMonth): number {
  return toAbsolute(ym) - toAbsolute(start);
}

export function yearMonthAt(start: YearMonth, index: MonthIndex): YearMonth {
  return addMonths(start, index);
}

/** Calendar month (0 = January) of the given step. */
export function calendarMonthAt(start: YearMonth, index: MonthIndex): number {
  return (toAbsolute(start) + index) % 12;
}

/** UTC midnight on the first day of the step's month. For the UI/chart edge only. */
export function dateAt(start: YearMonth, index: MonthIndex): Date {
  const { year, month } = parseYearMonth(start);
  return new Date(Date.UTC(year, month - 1 + index, 1));
}

/** Shared time axis for charts: one `Date` per simulated month. */
export function timeAxis(start: YearMonth, months: number): Date[] {
  const { year, month } = parseYearMonth(start);
  return Array.from({ length: months }, (_, i) => new Date(Date.UTC(year, month - 1 + i, 1)));
}
